import React, { useEffect, useRef, useState } from 'react';
import { pick, values } from 'lodash';
import { useSelector } from 'react-redux';
import { Box, IconButton, Skeleton, Tabs } from '@mui/material';
import cx from 'classnames';
import { KeyboardArrowLeftRounded, KeyboardArrowRightRounded } from '@mui/icons-material';
import { ScreenComponent, ScreenConfig } from 'new/routes/RouterManager';
import { ROUTE_DATA_PRODUCTS } from 'new/constants/pathnames';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { BannerDescription } from 'new/elements/BannerDescription';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import SearchBar from 'js/components/MacroInput/SearchBar';
import { AvailableFilterTypesByView, SearchFilterViews } from 'js/constants/visibilityFilters';
import Constants from 'js/Constants';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  listSelectStyles,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import {
  resetPageNumbers,
  setContextualSupport,
  setSortCriteria,
  unsetContextualSupport,
  loadDomainItems,
  loadOutgoingAccessRequests,
  setTotalPages,
  setTotalItems,
} from 'js/actions';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import styles from './DataProducts.module.scss';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_ALL_DOMAINS_REQUEST, GET_DOMAIN_ITEMS_REQUEST } from 'js/constants/actionTypes';
import CardsListPageNumbersContainer from 'js/components/elements/cards/CardsListPageNumbersContainer';
import { ExclusiveButtons } from 'new/elements/ExclusiveButtons';
import { SUPPORT_KEY_DATASET_LIST } from 'js/constants/supportPageKeys';
import { DataProductsDrawer } from '../components/DataProductsDrawer';
import { ModalRateDataProduct } from 'new/elements/ModalRateDataProduct';
import { ModalRequestAccess } from 'new/elements/ModalRequestAccess/ModalRequestAccess';
import useURLSearchParams from 'new/hooks/useURLSearchParams';
import { areSegmentsDifferent } from 'js/lib/SearchFilterUtils';
import { useMarketplaceDomains } from 'new/hooks/adapters/useMarketplaceDomains';
import { useAppListing } from 'new/hooks/useAppListing';
import MainContent from './MainContent';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import useAppDispatch from 'js/hooks/useAppDispatch';
import ResourceEditProvider from 'js/components/ResourceEditProvider';

interface DataProductsProps {}

const description =
  'Discovering data within your organization while maintaining strict privacy standards presents a formidable challenge. However, Data Products that incorporate the producer-consumer model offer a viable solution.';

export const DataProducts: ScreenComponent<DataProductsProps> = (props: DataProductsProps) => {
  const dispatch = useAppDispatch();
  const isLoadingDomains = useIsLoading(GET_ALL_DOMAINS_REQUEST);
  const isLoadingItems = useIsLoading(GET_DOMAIN_ITEMS_REQUEST);
  const prevFilters = useRef();
  const { domainEntitiesById, itemsByDomain } = useMarketplaceDomains();
  const { currentPage, sortCriteria } = useAppListing();
  const { params, updateUrl } = useURLSearchParams('domain_id');
  const { filters: allFilters, availableFilters: allAvailableFilters } = useSelector(
    (state: any) => state.app.visibilityFilters,
  );
  const availableFilterTypes = AvailableFilterTypesByView[SearchFilterViews.AVAILABLE_DATA_SETS];
  const filters = allFilters[SearchFilterViews.AVAILABLE_DATA_SETS];
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedNexset, setSelectedNexset] = useState(null);

  useEffect(() => {
    dispatch(loadOutgoingAccessRequests());
  }, []);

  useEffect(() => {
    dispatch(resetPageNumbers());
    dispatch(setContextualSupport(SUPPORT_KEY_DATASET_LIST));

    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_DATASET_LIST));
    };
  }, []);

  useEffect(() => {
    if (!isLoadingDomains && domainEntitiesById.length) {
      if (params.domain_id !== null) {
        const domainId = params.domain_id;
        onDomainChanged(domainId);
      } else if (domainEntitiesById[0]) {
        const domainId = domainEntitiesById[0]?.id;
        onDomainChanged(domainId);
      }
    }
  }, [isLoadingDomains]);

  useEffect(() => {
    if (filters && prevFilters.current && areSegmentsDifferent(filters, prevFilters.current)) {
      loadData(selectedDomain);
    }

    prevFilters.current = filters;
  }, [filters]);

  useEffect(() => {
    loadData(selectedDomain);
  }, [currentPage, sortCriteria]);

  const sortCriteriaOptions = values(Constants.SortCriteriaInfo);
  const selectedSortOption = getCurrentSelectedOption(sortCriteriaOptions, sortCriteria);

  const onSortCriteriaChanged = (selectedOption) => {
    dispatch(setSortCriteria(getNewSelectionValue(selectedOption, true, '')));
  };

  const loadData = (domainId) => {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);
    if (domainId) {
      dispatch(loadDomainItems(domainId, currentPage || 1, sortCriteria, availableFilters)).then(
        (response: { totalPages: number; totalItems: number }) => {
          const { totalPages, totalItems } = response;
          if (!totalItems && !totalPages) return;

          dispatch(setTotalPages(totalPages));
          dispatch(setTotalItems(totalItems));
        },
      );
    }
  };

  const onDomainChanged = (value) => {
    if (value && value !== selectedDomain) {
      setSelectedDomain(value);
      updateUrl({ domain_id: value });
      loadData(value);
    }
  };

  const onCloseDrawer = () => {
    setSelectedNexset(null);
  };

  if (!isLoadingDomains && (!domainEntitiesById || !domainEntitiesById.length)) {
    return (
      <>
        <BannerDescription title="Data Products" description={description} />
        <NoContentTextView
          title={<div>No Data Domains have been created for the Private Data Marketplace.</div>}
        />
      </>
    );
  }

  return (
    <ResourceEditProvider>
      <DataProductsDrawer
        domainId={selectedDomain}
        datasetId={selectedNexset?.id}
        onClose={onCloseDrawer}
      />
      <BannerDescription title="Data Products" description={description} />
      {!isLoadingDomains && domainEntitiesById?.length > 0 ? (
        <Box className={styles.domainButtonContainer}>
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={selectedDomain}
            onChange={(event: React.SyntheticEvent, value: number) => onDomainChanged(value)}
            xs={{ height: '36px' }}
            ScrollButtonComponent={(props) => {
              if (props.direction === 'left' && !props.disabled) {
                return (
                  <IconButton {...props}>
                    <KeyboardArrowLeftRounded className={cx(styles.scroll_icon, styles.left)} />
                  </IconButton>
                );
              } else if (props.direction === 'right' && !props.disabled) {
                return (
                  <IconButton {...props}>
                    <KeyboardArrowRightRounded
                      className={`${styles.scroll_icon} ${styles.right}`}
                    />
                  </IconButton>
                );
              }
            }}
            TabIndicatorProps={{
              style: { display: 'none' },
            }}
          >
            <ExclusiveButtons
              data={domainEntitiesById?.map((item) => ({
                label: item?.name,
                value: item?.id,
              }))}
              value={selectedDomain}
            />
          </Tabs>
        </Box>
      ) : (
        <Box className={styles.domainButtonLoader}>
          {new Array(12).fill(0).map((el, index) => (
            <Skeleton width={100} height={36} key={index} />
          ))}
        </Box>
      )}
      <div className={styles.controlsContainer}>
        <MacroOptionsContextWrap>
          <SearchBar
            view={SearchFilterViews.AVAILABLE_DATA_SETS}
            availableFilterTypes={AvailableFilterTypesByView[SearchFilterViews.AVAILABLE_DATA_SETS]}
          />
        </MacroOptionsContextWrap>
        <UnsortedSelect
          options={sortCriteriaOptions}
          components={{ Input: SelectInput, Option: SelectOption }}
          isClearable={false}
          isSearchable={true}
          backspaceRemovesValue={false}
          value={selectedSortOption}
          onChange={onSortCriteriaChanged}
          styles={listSelectStyles}
        />
        <CardsListPageNumbersContainer isLoading={isLoadingItems} />
      </div>
      <MainContent
        items={itemsByDomain[selectedDomain]}
        domain={selectedDomain}
        isLoading={isLoadingItems}
        setSelectedItem={setSelectedNexset}
      />
    </ResourceEditProvider>
  );
};

DataProducts.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_DATA_PRODUCTS}`,
  domain: 'marketplace',
  modals: [ModalRateDataProduct, ModalRequestAccess],
  header: {
    title: 'Data Products',
    slugTitle: 'Data Products',
  },
  screenName: 'DataProducts',
} as ScreenConfig<DataProductsProps>;
